import { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import API from '../lib/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ChartBar, Users, Robot, GearSix, PencilSimple, Megaphone,
  CalendarBlank, FileText, UserList, DeviceMobile, Coins,
  SignOut, List, Sun, Moon, CaretLeft, Broadcast, LockKey,
  ChatCircleDots, ChatTeardropText, ChatsTeardrop, UserCheck, X, Timer, WhatsappLogo, Plug, BellRinging, Sliders, Heartbeat
} from '@phosphor-icons/react';

const globalMenuItems = [
  { path: '/app/dashboard', label: 'Dashboard', icon: ChartBar, roles: ['super_admin', 'admin'], perm: 'dashboard' },
  { path: '/app/bot-automation', label: 'Bot Automation', icon: Robot, roles: ['super_admin', 'admin', 'staff'], perm: 'bot_automation' },
  { path: '/app/reports', label: 'Reports', icon: FileText, roles: ['super_admin', 'admin', 'staff'], perm: 'reports' },
  { path: '/app/bot-live-chat?tab=assigned', label: 'Assigned Chats', icon: UserCheck, roles: ['super_admin', 'admin', 'staff'], hasBadge: 'assigned', perm: 'assigned_chats' },
  { path: '/app/chat', label: 'Support Chat', icon: ChatCircleDots, roles: ['super_admin', 'admin', 'staff'], hasBadge: 'adminChat', perm: 'admin_chat' },
  { path: '/app/bot-live-chat', label: 'All Live Chat', icon: ChatsTeardrop, roles: ['super_admin', 'admin', 'staff'], perm: 'live_chat', hasBadge: 'liveChat' },
  { path: '/app/whatsapp-chat', label: 'WhatsApp Chat', icon: WhatsappLogo, roles: ['super_admin', 'admin', 'staff'], perm: 'whatsapp_chat', hasBadge: 'whatsapp' },
  { path: '/app/whatsapp-apis', label: 'WhatsApp APIs', icon: Plug, roles: ['super_admin', 'admin'] },
  { path: '/app/admin-alerts', label: 'Instant Alerts', icon: BellRinging, roles: ['super_admin'] },
  { path: '/app/admins', label: 'Admins', icon: Users, roles: ['super_admin'] },
  { path: '/app/system-health', label: 'System & Health', icon: Heartbeat, roles: ['super_admin'] },
  { path: '/app/bots', label: 'Bots', icon: Robot, roles: ['super_admin'] },
  { path: '/app/credits', label: 'Credits', icon: Coins, roles: ['super_admin', 'admin', 'staff'], perm: 'credits' },
  { path: '/app/staff', label: 'Staff', icon: UserList, roles: ['super_admin', 'admin'] },
];

const botMenuItems = [
  { path: '/app/bot-automation', label: 'Bot Automation', perm: 'bot_automation', icon: Robot },
  { path: '/app/edit-bot', label: 'Edit Bot', perm: 'edit_bots', icon: PencilSimple },
  { path: '/app/bot-settings', label: 'Bot Settings', perm: 'bot_settings', icon: GearSix },
  { path: '/app/broadcast', label: 'Broadcast', perm: 'broadcast', icon: Megaphone },
  { path: '/app/channel-broadcast', label: 'Channel Broadcast', perm: 'channel_broadcast', icon: Broadcast },
  { path: '/app/scheduled-broadcast', label: 'Scheduled', perm: 'scheduled_broadcast', icon: CalendarBlank },
  { path: '/app/broadcast-reports', label: 'Reports', perm: 'broadcast_reports', icon: FileText },
  { path: '/app/user-reports', label: 'Users', perm: 'user_reports', icon: UserList },
  { path: '/app/mobile-reports', label: 'Mobile', perm: 'mobile_reports', icon: DeviceMobile },
  { path: '/app/bot-live-chat', label: 'All Live Chat', perm: 'live_chat', icon: ChatTeardropText, hasBadge: 'botChat' },
  { path: '/app/auto-messages', label: 'Auto Messages', perm: 'auto_messages', icon: Timer },
];

export default function Sidebar({ collapsed, setCollapsed, bots, selectedBot, onSelectBot }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [changingPwd, setChangingPwd] = useState(false);
  const [unreadChat, setUnreadChat] = useState(0);
  const [unreadSuperChat, setUnreadSuperChat] = useState(0);
  const [unreadStaffChat, setUnreadStaffChat] = useState(0);
  const [unreadAssigned, setUnreadAssigned] = useState(0);
  const [unreadLiveChat, setUnreadLiveChat] = useState(0);
  const [unreadBotChat, setUnreadBotChat] = useState(0);
  const [unreadWhatsapp, setUnreadWhatsapp] = useState(0);
  const [botUnreads, setBotUnreads] = useState({});
  const prevAssignedRef = useRef(0);

  const playBeep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.25);
    } catch {}
  };

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const fetchUnread = useCallback(() => {
    API.get('/chat/unread').then(r => {
      setUnreadChat(r.data.unread || 0);
      setUnreadSuperChat(r.data.super_admin_unread || 0);
      setUnreadStaffChat(r.data.staff_admin_unread || 0);
    }).catch(() => {});
    API.get('/chat/assigned-unread').then(r => {
      const cnt = r.data.unread || 0;
      if (cnt > prevAssignedRef.current && prevAssignedRef.current > 0) {
        playBeep();
      }
      prevAssignedRef.current = cnt;
      setUnreadAssigned(cnt);
    }).catch(() => {});
    API.get('/chat/live-unread').then(r => {
      setUnreadLiveChat(r.data.unread || 0);
      if (r.data.bot_unreads) setBotUnreads(r.data.bot_unreads);
    }).catch(() => {});
    if (selectedBot) API.get(`/bot-chat/${selectedBot}/unread`).then(r => setUnreadBotChat(r.data.unread || 0)).catch(() => {});
    API.get('/whatsapp/unread').then(r => setUnreadWhatsapp(r.data.unread || 0)).catch(() => {});
  }, [selectedBot]);
  useEffect(() => {
    fetchUnread();
    const t = setInterval(fetchUnread, 5000);
    const handleChatRead = () => fetchUnread();
    window.addEventListener('chat-read', handleChatRead);
    return () => {
      clearInterval(t);
      window.removeEventListener('chat-read', handleChatRead);
    };
  }, [fetchUnread]);

  const filteredGlobal = globalMenuItems.filter(i => {
    if (!i.roles.includes(user?.role)) return false;
    if (user?.role === 'super_admin') return true;
    if (user?.role === 'admin' && i.perm) return user?.permissions?.includes(i.perm);
    if (user?.role === 'staff' && i.perm) {
      return user?.permissions?.includes(i.perm);
    }
    return true;
  });
  const filteredBot = botMenuItems.filter(i => {
    if (user?.role === 'super_admin') return true;
    if (!i.perm) return true;
    if (i.perm.endsWith('_reports') && user?.permissions?.includes('reports')) return true;
    if (i.perm === 'channel_broadcast' && user?.permissions?.includes('broadcast')) return true;
    if (i.perm === 'scheduled_broadcast' && user?.permissions?.includes('broadcast')) return true;
    return user?.permissions?.includes(i.perm);
  });

  const handleChangePwd = async (e) => {
    e.preventDefault(); setChangingPwd(true);
    try { await API.post('/auth/change-password', { current_password: currentPwd, new_password: newPwd }); toast.success('Password changed'); setPwdOpen(false); setCurrentPwd(''); setNewPwd(''); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setChangingPwd(false); }
  };

  const handleLogout = async () => { await logout(); window.location.href = '/login'; };
  const initials = (user?.name || user?.email || '?').slice(0, 2).toUpperCase();
  const selectedBotObj = bots.find(b => b.id === selectedBot);

  const getBadge = (item) => {
    if (item.hasBadge === 'assigned' && unreadAssigned > 0) return unreadAssigned;
    if (item.hasBadge === 'adminChat') {
      const total = (unreadChat || 0) + (unreadSuperChat || 0) + (unreadStaffChat || 0);
      return total;
    }
    if (item.hasBadge === 'liveChat' && unreadLiveChat > 0) return unreadLiveChat;
    if (item.hasBadge === 'botChat') {
      return unreadBotChat > 0 ? unreadBotChat : (unreadLiveChat > 0 ? unreadLiveChat : 0);
    }
    if (item.hasBadge === 'whatsapp' && unreadWhatsapp > 0) return unreadWhatsapp;
    return 0;
  };

  const NavItem = ({ item }) => {
    const Icon = item.icon;
    const badge = getBadge(item);
    const [itemBase, itemQuery] = item.path.split('?');
    const isCurrentActive = itemQuery
      ? location.pathname === itemBase && location.search.includes(itemQuery)
      : location.pathname === itemBase && (!location.search || !location.search.includes('tab=assigned'));

    return (
      <NavLink to={item.path} data-testid={`nav-${item.path.split('/').pop().replace(/[^a-zA-Z0-9_-]/g, '-')}`}
        className={`flex items-center gap-2.5 px-3 py-1.5 rounded-md text-[13px] transition-all duration-150 ${isCurrentActive ? 'bg-primary text-primary-foreground font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-accent'} ${collapsed ? 'justify-center' : ''}`}
        title={collapsed ? item.label : undefined}>
        <Icon size={18} weight={isCurrentActive ? 'bold' : 'regular'} />
        {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
        {badge > 0 && <span className="px-1.5 py-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[18px] text-center">{badge > 99 ? '99+' : badge}</span>}
      </NavLink>
    );
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Top: Logo + Collapse */}
      <div className="p-3 border-b border-border flex items-center justify-between">
        {!collapsed ? (
          <div className="flex items-center gap-2.5 min-w-0">
            <img src="/logo.png" alt="Telegram Bot" className="w-6 h-6 object-contain rounded-md shrink-0 shadow-xs" />
            <span className="text-base font-bold tracking-tight truncate" style={{ fontFamily: 'Cabinet Grotesk' }}>Telegram Bot</span>
          </div>
        ) : (
          <div className="mx-auto">
            <img src="/logo.png" alt="Telegram Bot" className="w-6 h-6 object-contain rounded-md shrink-0 shadow-xs" />
          </div>
        )}
        <button data-testid="sidebar-collapse-btn" onClick={() => setCollapsed(!collapsed)} className="p-1 rounded hover:bg-accent hidden lg:flex ml-auto">
          <CaretLeft size={14} className={`transition-transform ${collapsed ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
        {filteredGlobal.map(item => <NavItem key={item.path} item={item} />)}

        {/* Bot selector for admins/staff (when no bot selected) */}
        {!selectedBot && (user?.role === 'admin' || user?.role === 'staff') && bots.length > 0 && (
          <>
            {!collapsed && (
              <div className="pt-4 pb-1 px-1">
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">Your Bots</span>
              </div>
            )}
            {bots.map(b => (
              <button key={b.id} onClick={() => onSelectBot(b.id)} data-testid={`admin-select-bot-${b.id}`}
                className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] text-muted-foreground hover:text-foreground hover:bg-accent transition-colors ${collapsed ? 'justify-center' : ''}`}>
                <div className={`w-2 h-2 rounded-full shrink-0 ${b.is_active ? 'bg-green-500' : 'bg-red-400'}`} />
                {!collapsed && <span className="truncate flex-1 text-left">@{b.bot_username || b.bot_name}</span>}
                {!collapsed && (botUnreads[b.id] || 0) > 0 && (
                  <span className="px-1.5 py-0.2 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] text-center shrink-0">
                    {botUnreads[b.id]}
                  </span>
                )}
              </button>
            ))}
          </>
        )}

        {/* Bot-specific section */}
        {selectedBot && selectedBotObj && (
          <>
            {!collapsed && (
              <div className="pt-4 pb-1 flex items-center justify-between px-1">
                <div className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${selectedBotObj.is_active ? 'bg-green-500' : 'bg-red-400'}`} />
                  <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground truncate max-w-[130px]">@{selectedBotObj.bot_username || selectedBotObj.bot_name}</span>
                </div>
                <button onClick={() => onSelectBot(null)} className="p-0.5 rounded hover:bg-accent" data-testid="close-bot-btn"><X size={12} className="text-muted-foreground" /></button>
              </div>
            )}
            {filteredBot.map(item => <NavItem key={item.path} item={item} />)}
          </>
        )}
      </nav>

      {/* Bottom: Profile + Actions */}
      <div className="border-t border-border p-2 space-y-0.5">
        <button data-testid="change-password-btn" onClick={() => setPwdOpen(true)}
          className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-md text-[13px] text-muted-foreground hover:text-foreground hover:bg-accent transition-colors ${collapsed ? 'justify-center' : ''}`}>
          <LockKey size={18} />
          {!collapsed && <span>Change Password</span>}
        </button>
        <button data-testid="theme-toggle-btn" onClick={toggleTheme}
          className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-md text-[13px] text-muted-foreground hover:text-foreground hover:bg-accent transition-colors ${collapsed ? 'justify-center' : ''}`}>
          {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
          {!collapsed && <span>{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>}
        </button>
        <button data-testid="logout-btn" onClick={handleLogout}
          className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-md text-[13px] text-destructive hover:bg-destructive/10 transition-colors ${collapsed ? 'justify-center' : ''}`}>
          <SignOut size={18} />
          {!collapsed && <span>Logout</span>}
        </button>
        {!collapsed && user && (
          <div className="px-3 py-1.5 text-[11px] text-muted-foreground truncate">
            {user.name || user.email}
            <span className="block text-[10px] uppercase tracking-wider">{user.role?.replace('_', ' ')}</span>
          </div>
        )}
      </div>

      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><LockKey size={20} /> Change Password</DialogTitle></DialogHeader>
          <form onSubmit={handleChangePwd} className="space-y-4">
            <div className="space-y-1.5"><Label>Current Password</Label><Input data-testid="current-password-input" type="password" value={currentPwd} onChange={e => setCurrentPwd(e.target.value)} required /></div>
            <div className="space-y-1.5"><Label>New Password</Label><Input data-testid="new-password-input" type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} required minLength={6} /></div>
            <DialogFooter><Button data-testid="change-pwd-submit" type="submit" disabled={changingPwd}>{changingPwd ? 'Changing...' : 'Change Password'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );

  return (
    <>
      <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)} className="lg:hidden fixed top-3 left-3 z-50 p-2 bg-background border border-border rounded-md chat-active-hide"><List size={22} /></button>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileOpen(false)}>
          <aside className="w-64 h-full bg-background border-r border-border" onClick={e => e.stopPropagation()}>{sidebarContent}</aside>
        </div>
      )}
      <aside className={`hidden lg:flex flex-col border-r border-border bg-background transition-all duration-300 ${collapsed ? 'w-16' : 'w-60'} h-screen sticky top-0`}>{sidebarContent}</aside>
    </>
  );
}
